import { useState } from 'react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { ValueViewerProps } from 'common/types/scalarSpreadsheet';
import type { CellAnnotationCellKey } from 'components/CellAnnotationValueViewer/types';
import FastValueViewer from 'components/ScalarSpreadsheet/utilities/FastValueViewer';
import type { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { useCellAnnotationStore } from 'store/useCellAnnotationStore';
import MoneyChip from './MoneyChip';

// Inline cell decoration: renders a currency badge to the left of the host
// cell's normal value rendering. Used wherever a money cell carries a Pre-FX
// annotation (the chip shows the source currency while the amount stays in
// the host currency), and inside the Pre-FX popout's single-cell display.
//
// The chip is purely a visual indicator here. Edits to the source currency
// happen via the reaction-bar / popout flow; this viewer never owns the
// chip's interactivity. See `MoneyChip` for the interactive variant.

const ViewerLayout = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  width: '100%',
  // Cover the cell's full value area so the hover target (onMouseEnter/Leave)
  // matches the whole cell, not just the text baseline.
  height: '100%',
  // The chip is fixed-size; the amount takes the rest. min-width 0 lets the
  // amount truncate gracefully inside narrow ledger columns.
  minWidth: 0,
});

const AmountContainer = styled(Box)({
  flex: 1,
  minWidth: 0,
});

const ChipClickWrapper = styled(Box)({
  display: 'inline-flex',
  cursor: 'pointer',
});

// Resolve a chip code from `cell.currency` regardless of whether the host
// passed a string code (from a flat customFormat) or a full `RowFormat.currency`
// object (from a LedgerTable). Normalizing here keeps callers from threading
// the same lookup through every parser.
const resolveCurrencyCode = (currency: unknown): string | undefined => {
  if (typeof currency === 'string') {
    return currency;
  }
  if (currency && typeof currency === 'object' && 'code' in currency) {
    const { code } = currency as { code?: unknown };
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
};

// Format an exchange rate to the same precision the AlternateCurrencyChip /
// CurrencyAdjustedValueViewer / getExchangeRateTooltip helpers used to
// render — e.g. `0.009631963`. Up to 6 significant decimal digits; trim
// trailing zeros so the tooltip stays readable for whole-number rates.
const formatRate = (rate: number): string => {
  if (!Number.isFinite(rate)) return String(rate);
  // toPrecision keeps significant figures, toString cleans up trailing zeros
  // when the value falls inside the regular decimal range.
  const fixed = rate.toFixed(9);
  return parseFloat(fixed).toString();
};

const coerceNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

interface PreFxAnnotationRow {
  value?: unknown;
  metadata?: { source_currency?: string } | Record<string, unknown> | null;
}

interface PreFxAnnotationState {
  rows?: PreFxAnnotationRow[];
}

// Derive the chip's tooltip text from the persisted Pre-FX annotation. The
// store already holds (source_amount, source_currency); the host cell holds
// the converted host amount. rate = host / source. Identity (source ==
// host) or any missing/invalid data falls back to today's `Currency: X`
// label so cells without a Pre-FX annotation still get a sane tooltip.
const buildPreFxTooltip = (
  hostCode: string,
  hostAmount: unknown,
  annotationState: PreFxAnnotationState | undefined,
): string => {
  const firstRow = annotationState?.rows?.[0];
  if (!firstRow) return `Currency: ${hostCode}`;
  const sourceAmount = coerceNumber(firstRow.value);
  const metadata = (firstRow.metadata ?? {}) as { source_currency?: string };
  const sourceCurrency = metadata.source_currency;
  const host = coerceNumber(hostAmount);
  if (!sourceCurrency || sourceAmount === null || host === null) {
    return `Currency: ${hostCode}`;
  }
  if (sourceCurrency === hostCode || sourceAmount === 0) {
    return `Currency: ${hostCode}`;
  }
  const rate = host / sourceAmount;
  if (!Number.isFinite(rate) || rate <= 0) {
    return `Currency: ${hostCode}`;
  }
  return `1 ${sourceCurrency} -> ${formatRate(rate)} ${hostCode}`;
};

const useTooltip = (
  code: string,
  hostAmount: unknown,
  cellKey: CellAnnotationCellKey | undefined,
): string => {
  const annotationState = useCellAnnotationStore(
    state => (cellKey ? state.states[cellKey] : undefined) as PreFxAnnotationState | undefined,
  );
  return buildPreFxTooltip(code, hostAmount, annotationState);
};

// The entered (pre-FX source) currency for this cell, read from the persisted
// annotation. Undefined when nothing has been entered yet (the chip then acts
// purely as the entry point to add one).
export const useEnteredCurrency = (cellKey: CellAnnotationCellKey | undefined): string | undefined =>
  useCellAnnotationStore(state => {
    const firstRow = (cellKey ? state.states[cellKey] : undefined)?.rows?.[0] as PreFxAnnotationRow | undefined;
    const sourceCurrency = (firstRow?.metadata as { source_currency?: string } | null | undefined)?.source_currency;
    return typeof sourceCurrency === 'string' ? sourceCurrency : undefined;
  });

// Decide whether the in-cell currency chip should render (SCAL-9062).
// Hover parity with the citation footnote: whenever the cell is `active`
// (hovered OR selected) the chip ALWAYS shows, because it is the edit
// affordance - the only entry point back into the Pre-FX popout. An identity
// annotation (source == host, the popout's seeded default) would otherwise be
// a dead end: suppressing it at rest is fine, suppressing it while active would
// make the annotation impossible to edit or clear.
//
// At-rest suppression (cell not active):
//   - No currency code resolved -> never (nothing to show).
//   - Entered currency equals the host currency -> hidden: identity carries no
//     FX signal, so the resting cell stays clean.
//   - `chipActiveOnly` cells (cap-table OIP) -> hidden at rest, revealed only on
//     hover/selection. Ledger cells (chipActiveOnly unset) keep a
//     differing-currency chip visible at rest.
export const shouldShowChip = (
  code: string | undefined,
  enteredCurrency: string | undefined,
  active: boolean,
  chipActiveOnly: boolean | undefined,
): boolean => {
  if (!code) return false;
  if (active) return true; // the chip is the edit affordance
  if (enteredCurrency === code) return false; // identity carries no FX signal at rest
  return !chipActiveOnly; // OIP: hidden at rest; ledger cells: visible
};

// Single source of truth for "is the currency chip currently rendered on this
// cell?". Resolves the host code, reads the entered currency, and applies the
// same gating the viewers use. CitationValueViewer consumes this to shift the
// citation footnote clear of the chip so the two don't overlap (SCAL-9062).
// Callers pass `active` = hovered OR selected, matching the viewers' own
// reveal-on-hover/selection behavior so the offset engages exactly when the
// chip is visible.
//
// Only cells wired through applyPreFxAnnotation carry the chip layout, and
// those always set `cellAnnotationCellKey`. Gate on it so a plain cited cell
// that merely has a `cell.currency` (but no chip wrapper) never reports a chip.
export const useChipPresent = (
  cell: Pick<ICell, 'currency' | 'cellAnnotationCellKey' | 'chipActiveOnly'>,
  active: boolean,
): boolean => {
  const code = resolveCurrencyCode(cell.currency);
  const enteredCurrency = useEnteredCurrency(cell.cellAnnotationCellKey);
  if (!cell.cellAnnotationCellKey) {
    return false;
  }
  return shouldShowChip(code, enteredCurrency, active, cell.chipActiveOnly);
};

/**
 * Default value-viewer variant: reads its inner viewer from
 * `cell.originalValueViewer` (falling back to FastValueViewer). Use this
 * when the cell already exposes an `originalValueViewer` attribute — e.g.
 * when this viewer itself is mounted via `cell.originalValueViewer`-style
 * wrapping (the CellAnnotationValueViewer ladder).
 */
const MoneyChipValueViewer: React.FC<ValueViewerProps> = ({ cell, selected, ...rest }) => {
  const [isHovered, setIsHovered] = useState(false);
  const code = resolveCurrencyCode(cell.currency);
  const tooltip = useTooltip(code ?? '', cell.value, cell.cellAnnotationCellKey);
  const enteredCurrency = useEnteredCurrency(cell.cellAnnotationCellKey);
  const active = isHovered || Boolean(selected);
  const showChip = shouldShowChip(code, enteredCurrency, active, cell.chipActiveOnly);
  const InnerViewer = cell.originalValueViewer ?? FastValueViewer;
  return (
    <ViewerLayout onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      {showChip && code && <MoneyChip value={code} tooltip={tooltip} />}
      <AmountContainer>
        <InnerViewer cell={cell} selected={selected} {...rest} />
      </AmountContainer>
    </ViewerLayout>
  );
};

/**
 * HOC: wrap a specific inner viewer with the money-chip layout. Use this
 * when the cell's `originalValueViewer` is already reserved (e.g. because
 * `CellAnnotationValueViewer` is the outer wrapper on the same cell, so
 * `originalValueViewer` points to the chip-decorated viewer rather than the
 * column-level number renderer).
 *
 * Example: CFL Amount cell composition is
 *   `CellAnnotationValueViewer` (annotation chrome)
 *     ↳ `originalValueViewer` = `withMoneyChip(AmountValueViewer)` (chip)
 *       ↳ `AmountValueViewer` (NumberMultipleCheckbox / plain text)
 */
export const withMoneyChip = (InnerViewer: React.ComponentType<ValueViewerProps>) => {
  const Wrapped: React.FC<ValueViewerProps> = ({ cell, selected, ...rest }) => {
    const [isHovered, setIsHovered] = useState(false);
    const code = resolveCurrencyCode(cell.currency);
    const { onChipClick } = cell;
    const tooltip = useTooltip(code ?? '', cell.value, cell.cellAnnotationCellKey);
    const enteredCurrency = useEnteredCurrency(cell.cellAnnotationCellKey);
    const active = isHovered || Boolean(selected);
    const showChip = shouldShowChip(code, enteredCurrency, active, cell.chipActiveOnly);
    return (
      <ViewerLayout onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
        {showChip && code &&
          (onChipClick ? (
            <ChipClickWrapper
              onClick={() => onChipClick(cell)}
              onMouseDown={event => event.stopPropagation()}
              role="button"
              aria-label="Change currency"
              tabIndex={0}>
              <MoneyChip value={code} tooltip={tooltip} />
            </ChipClickWrapper>
          ) : (
            <MoneyChip value={code} tooltip={tooltip} />
          ))}
        <AmountContainer>
          <InnerViewer cell={cell} selected={selected} {...rest} />
        </AmountContainer>
      </ViewerLayout>
    );
  };
  Wrapped.displayName = `withMoneyChip(${InnerViewer.displayName ?? InnerViewer.name ?? 'Component'})`;
  return Wrapped;
};

export default MoneyChipValueViewer;
